import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Button, Chip, Container, Paper, Stack, Typography } from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import LocalPhoneRoundedIcon from "@mui/icons-material/LocalPhoneRounded";
import SEOHead from "../components/SEOHead";
import Reveal from "../components/Reveal";
import { clinic } from "../constants/clinic";

const About = () => {
  const aboutStructuredData = {
    "@context": "https://schema.org",
    "@type": "ProfilePage",
    name: "院長紹介｜わかば接骨院",
    description: "わかば接骨院の理念と、院長 住田雅美のご紹介。",
    url: `${clinic.domain}/about`,
    mainEntity: {
      "@type": "Person",
      name: "住田雅美",
      jobTitle: "院長・柔道整復師・鍼灸師",
      worksFor: { "@type": "MedicalClinic", name: clinic.name },
      knowsAbout: ["柔道整復", "鍼灸", "運動療法", "腰痛施術", "五十肩施術"],
      hasCredential: ["柔道整復師", "鍼灸師"],
      image: `${clinic.domain}/picture_of_doctor.webp`,
    },
  };

  return (
    <>
      <SEOHead
        title="当院について・院長紹介"
        description="瀬戸市で36年の経験を持つ、わかば接骨院 院長 住田雅美をご紹介。柔道整復師・鍼灸師として、一人ひとりに合う丁寧な施術を大切にしています。"
        keywords="わかば接骨院 院長,住田雅美,瀬戸市 柔道整復師,瀬戸市 鍼灸師"
        canonical="/about"
        structuredData={aboutStructuredData}
      />

      <Box component="section" sx={{ bgcolor: "#E8F0E8", py: { xs: 7, md: 10 }, overflow: "hidden" }}>
        <Container maxWidth="lg">
          <Reveal>
            <Chip label="ABOUT US" sx={{ mb: 2, bgcolor: "white", color: "primary.dark", fontWeight: 700, letterSpacing: ".12em" }} />
            <Typography component="h1" variant="h1" sx={{ fontSize: { xs: "2.35rem", md: "3.5rem" }, mb: 1.5 }}>当院について</Typography>
            <Typography color="text.secondary" sx={{ fontSize: { sm: "1.08rem" } }}>地域の皆さまと、健やかな毎日をつくる接骨院です。</Typography>
          </Reveal>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 13 } }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: ".9fr 1.1fr" }, gap: { xs: 5, md: 9 }, alignItems: "center" }}>
          <Reveal>
            <Box component="img" src="/picture_of_doctor.webp" alt="わかば接骨院 院長 住田雅美" fetchPriority="high" sx={{ width: "100%", maxHeight: 650, objectFit: "cover", objectPosition: "center 25%", borderRadius: "32px 120px 32px 32px" }} />
          </Reveal>
          <Reveal delay={100}>
            <Typography sx={{ color: "primary.dark", fontWeight: 700, letterSpacing: ".1em", mb: 1.2 }}>院長紹介</Typography>
            <Typography component="h2" variant="h2" sx={{ fontSize: { xs: "2rem", md: "2.75rem" }, mb: 2.5 }}>
              目の前の方にとって、<br />必要な施術を丁寧に。
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              わかば接骨院は、患者様一人ひとりの体の状態や暮らしに合わせた施術を大切にしています。
              丁寧な問診と確認を行い、今の状態と施術の方針を分かりやすくご説明します。
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              36年の経験を活かしながら、日々知識を更新し、安心してご相談いただける地域の接骨院であり続けたいと考えています。
            </Typography>
            <Typography variant="h5" sx={{ fontFamily: '"Noto Serif JP", serif' }}>院長　住田 雅美</Typography>
            <Typography color="primary.dark" sx={{ fontWeight: 700, mt: .5 }}>柔道整復師・鍼灸師</Typography>
            <Stack spacing={1.2} sx={{ mt: 3 }}>
              {["瀬戸市で36年の経験", "問診から施術まで院長が対応", "施術と運動療法を組み合わせたサポート"].map((text) => (
                <Stack key={text} direction="row" spacing={1} alignItems="center">
                  <CheckCircleRoundedIcon sx={{ color: "primary.dark", fontSize: 20 }} />
                  <Typography>{text}</Typography>
                </Stack>
              ))}
            </Stack>
          </Reveal>
        </Box>
      </Container>

      <Container maxWidth="md" sx={{ py: { xs: 8, md: 11 } }}>
        <Reveal>
          <Paper elevation={0} sx={{ p: { xs: 4, md: 6 }, border: "1px solid", borderColor: "divider", textAlign: "center" }}>
            <Typography variant="h3" sx={{ fontSize: { xs: "1.7rem", md: "2.1rem" }, mb: 1.5 }}>体のお悩み、まずはご相談ください</Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>ご予約・ご相談はお電話で承っています。</Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="center">
              <Button component="a" href={clinic.phoneHref} variant="contained" startIcon={<LocalPhoneRoundedIcon />}>{clinic.phone}</Button>
              <Button component={RouterLink} to="/services" variant="outlined" endIcon={<ArrowForwardRoundedIcon />}>施術内容を見る</Button>
            </Stack>
          </Paper>
        </Reveal>
      </Container>
    </>
  );
};

export default About;
